import {
  CdkDragDrop,
  moveItemInArray,
  CdkDropList,
  CdkDrag,
  CdkDragPlaceholder,
} from '@angular/cdk/drag-drop';
import { Component, Inject, OnInit, Optional } from '@angular/core';
import {
  FormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ButtonStyle } from '@constants/button/1.0';
import { ProductService } from '@services/product/1.0/product.service';
import { CompositeService } from '@services/composite/1.0/composite.service';
import { Subject, forkJoin, takeUntil } from 'rxjs';
import { ItemPrepGroupDialogData } from './index';
import { IsEmptyService } from '@services/utilities/1.0/is-empty.service';
import { ItemListComponent } from '../../item-list/1.0/item-list.component';
import { ButtonComponent } from '../../button/1.0/button.component';
import { NgIf, NgFor } from '@angular/common';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-item-prep-group-dialog',
  templateUrl: './item-prep-group-dialog.component.html',
  styleUrls: ['./item-prep-group-dialog.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ItemListComponent,
    ButtonComponent,
    NgIf,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    CdkDropList,
    NgFor,
    CdkDrag,
    CdkDragPlaceholder,
    TranslateModule,
  ],
})
export class ItemPrepGroupDialogComponent implements OnInit {
  brandId;
  buttonStyle = ButtonStyle;
  form: UntypedFormGroup;
  showError = false;
  isLoading = false;
  productList = [];
  addedItemsList = [];
  groupName: string;
  selectedItems = [];
  private unsubscribe = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<ItemPrepGroupDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: ItemPrepGroupDialogData,
    private formBuilder: FormBuilder,
    private compositeService: CompositeService,
    private productService: ProductService,
  ) {
    this.form = this.formBuilder.group({
      name: new UntypedFormControl(''),
    });
    this.brandId = sessionStorage.getItem('brand_id');
  }

  ngOnInit(): void {
    if (!IsEmptyService.isEmpty(this.data)) {
      this.form.get('name').setValue(this.data.name);
    }

    const productObs = this.productService
      .getProducts(this.brandId)
      .pipe(takeUntil(this.unsubscribe));

    const comboObs = this.compositeService
      .getComposites(this.brandId)
      .pipe(takeUntil(this.unsubscribe));

    this.isLoading = true;

    forkJoin([productObs, comboObs]).subscribe({
      next: ([products, combos]) => {
        this.productList = [].concat(products).concat(combos);
      },
      complete: () => {
        // iterate over incoming items
        for (let i = 0, iLen = (this.data?.items || []).length; i < iLen; i++) {
          let itemSku = this.data.items[i].sku;
          const productIndex = this.productList.findIndex(
            (item) => item.catalogEntryId === itemSku,
          );
          this.addedItemsList.push(
            this.productList.find((item) => item.catalogEntryId === itemSku),
          );
        }
        // clean preselection from left column
        for (let j = 0, jLen = this.addedItemsList.length; j < jLen; j++) {
          const addedItem = this.addedItemsList[j];
          this.productList = this.productList.filter((el) => el !== addedItem);
        }
        this.isLoading = false;
      },
    });
  }

  isDoneButtonDisabled() {
    if (this.isLoading) {
      return true;
    }

    if (this.data.nameNotRequired) {
      // items are required
      return this.addedItemsList.length === 0;
    }

    // name is required
    return !this.form.get('name').value;
  }

  onDone() {
    this.showError = false;
    this.selectedItems = [];
    this.dialogRef.close({
      name: this.form.get('name').value,
      items: this.addedItemsList,
      previousValue: this.data,
    });
  }

  onCancel() {
    this.showError = false;
    this.dialogRef.close(undefined);
  }

  onAdd() {
    this.selectedItems.forEach((e) => {
      this.addedItemsList.push(e);
      this.productList = this.productList.filter((el) => el !== e);
    });
    this.selectedItems = [];
  }

  onDelete(item) {
    this.addedItemsList = this.addedItemsList.filter(
      (el) => el.catalogEntryId !== item.catalogEntryId,
    );
    this.productList.push(item);
    this.productList = this.productList.slice().sort(compareItems);
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.addedItemsList, event.previousIndex, event.currentIndex);
  }

  getSelectedItems(eventData) {
    this.selectedItems = eventData;
  }
}

function compareItems(a, b) {
  return a.name > b.name ? 1 : -1;
}
